import { filter, fromEvent, map } from 'rxjs';
import fastdom from 'fastdom';
import Pill from './pill';

/**
 * @classdesc A class representing a component for managing the popular searches pills section.
 */
export default class PillsSection {
  /**
   * Creates an instance of PillsSection and connects it to what is currently on the DOM.
   * @param {string} popularSearchesPillsSelector The selector to identify the popular searches pills section.
   */
  constructor(popularSearchesPillsSelector) {
    this.popularSearchesPills = document?.querySelector(popularSearchesPillsSelector);
    this.pills = {};
    this.marginLeftArrowClass = 'nhs-u-margin--left-650';

    if (!this.popularSearchesPills) {
      throw new Error(`Popular searches pills section with selector '${popularSearchesPillsSelector}' not found.`);
    }

    // Select child elements that we need to control
    this.pillsList = this.popularSearchesPills?.querySelector('[data-popular-searches-pills-list]');
    this.scrollLeftButton = this.popularSearchesPills?.querySelector('[data-popular-searches-pills-button-left]');
    this.scrollRightButton = this.popularSearchesPills?.querySelector('[data-popular-searches-pills-button-right]');

    if (!this.pillsList || !this.scrollLeftButton || !this.scrollRightButton) {
      throw new Error('One or more required DOM elements inside the popular searches pills section are missing.');
    }

    // Scroll left or right on button clicks
    this.scrollLeftButton.addEventListener('click', () => {
      this.scrollHorizontally(-100);
    });

    this.scrollRightButton.addEventListener('click', () => {
      this.scrollHorizontally(100);
    });

    // Update left button visibility when scrolling pillsList.
    this.pillsList.addEventListener('scroll', () => {
      this.updateChildElementsVisibility();
    });

    // Initially, check and set the visibility of the left button
    this.setRigthLimit();
    this.updateChildElementsVisibility();
    this.attachPillListEvents();

    const onResize$ = fromEvent(window, 'resize');
    onResize$.subscribe(this.setRigthLimit.bind(this));
    onResize$.subscribe(this.updateChildElementsVisibility.bind(this));
  }

  /**
   * Updates pill elements based on the provided pillsDetails.
   * @param {{ name, text }} pillsDetails Details of the pills to be rendered.
   * @param Object popularPillFilters contains filters for each pill type
   */
  updatePillsOnDOM(pillsDetails, popularPillFilters) {
    this.pills = {};
    pillsDetails.forEach(({ name, text, isEnabled }) => {
      const isActive = popularPillFilters[name];
      const pill = new Pill({ name, text, isEnabled, isActive });
      this.pills[name] = pill;
    });

    fastdom.mutate(() => {
      this.pillsList.innerHTML = this.getHtmlPills();
      this.setRigthLimit();
      this.updateChildElementsVisibility();
      this.updatePillStatus(popularPillFilters);
      this.show();
    });
  }

  /**
   * Returns the activated pills text value selected on the page.
   */
  getActivatedPillsText() {
    const pillsActive = Array.from(
      this.pillsList.querySelectorAll('[data-popular-searches-pill-type]:checked ~ label')
    );
    const textPills = pillsActive.map((pill) => pill.innerText);

    return textPills;
  }

  /**
   * Sets the right limit of the scroll element to check when there is no more option to scroll.
   */
  setRigthLimit() {
    requestAnimationFrame(() => {
      this.limitRight = this.pillsList.getBoundingClientRect().right;
    });
  }

  /**
   * Returns the HTML representation of all the pills in an object.
   * @returns a string that represents the HTML pill elements.
   */
  getHtmlPills() {
    return Object.values(this.pills)
      .map((pill) => pill.getHtml())
      .join('');
  }

  /**
   * It updates the status of pills based on the provided pill filters.
   * @param pillFilters - The `pillFilters` parameter is an object that contains filters for each pill
   * type. The keys of the object represent the pill types, and the values represent whether the filter
   * is enabled or disabled for that pill type.
   */
  updatePillStatus(pillFilters) {
    const { activedPills, inactivedPills } = this.getPillsStatus(pillFilters);
    this.activePills(activedPills);
    this.inactivePills(inactivedPills);
  }

  /**
   * It takes in a `pillFilters` object and returns an object with two
   * arrays, `activePills` and `inactivePills`, which contain the keys of the `pillFilters` object
   * based on their values.
   * @param pillFilters - The `pillFilters` parameter is an object that contains filters for each pill
   * type. Each key in the object represents a pill type, and the corresponding value indicates whether
   * the pill is active or not.
   * @returns an object with two properties: "activedPills" and "inactivedPills".
   */
  getPillsStatus(pillFilters) {
    return Object.keys(this.pills).reduce(
      (accumulator, pillType) => {
        const isPillActive = pillFilters[pillType];
        if (isPillActive) {
          accumulator.activedPills.push(pillType);
          return accumulator;
        }
        accumulator.inactivedPills.push(pillType);
        return accumulator;
      },
      { activedPills: [], inactivedPills: [] }
    );
  }

  /**
   * It takes an array of pill types and checks each pill type in the "pills" object.
   * @param pills - The `pills` parameter is an array of pill types.
   */
  activePills(pills) {
    pills.forEach((pillType) => {
      this.pills[pillType].check();
    });
  }

  /**
   * It unchecks all the pills of the specified types.
   * @param pills - The `pills` parameter is an array that contains the types of pills that need to be
   * marked as inactive.
   */
  inactivePills(pills) {
    pills.forEach((pillType) => {
      this.pills[pillType].uncheck();
    });
  }

  /**
   * The function attaches click event listeners to a list of pills and dispatches a custom event with
   * information about the clicked pill.
   */
  attachPillListEvents() {
    fromEvent(this.pillsList, 'click')
      .pipe(
        map((event) => event.target.closest('[data-popular-searches-pill-type]')),
        filter(Boolean),
        map((pill) => ({
          pillType: pill.dataset.popularSearchesPillType,
          isChecked: pill.checked,
        }))
      )
      .subscribe((information) => {
        document.body.dispatchEvent(
          new CustomEvent('pillClickEvent', {
            detail: {
              data: information,
            },
          })
        );
      });
  }

  /**
   * Unhides the entire popular searches pills section.
   */
  show() {
    this.popularSearchesPills.removeAttribute('hidden');
  }

  /**
   * Hides the entire popular searches pills section.
   */
  hide() {
    this.popularSearchesPills.setAttribute('hidden', true);
  }

  /**
   * Scroll the `pillsList` horizontally.
   * @param {number} amount The distance to scroll. Positive values scroll right, negative values scroll left.
   */
  scrollHorizontally(amount) {
    const initialScrollLeft = this.pillsList.scrollLeft;
    const targetScrollLeft = initialScrollLeft + amount;
    const duration = 500; // Can adjust scroll speed here
    const startTime = performance.now();

    const step = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      if (elapsedTime < duration) {
        const newPosition = PillsSection.easeInOut(elapsedTime, initialScrollLeft, amount, duration);
        this.pillsList.scrollLeft = newPosition;
        requestAnimationFrame(step);
      } else {
        this.pillsList.scrollLeft = targetScrollLeft;
        this.updateChildElementsVisibility();
      }
    };

    requestAnimationFrame(step);
  }

  /**
   * Update visibility of left scroll button based on `pillsList` scroll position.
   * The ".marginLeft" let to the left scroll button to have a background white space when the user scroll to the right.
   */
  updateChildElementsVisibility() {
    requestAnimationFrame(() => {
      const isListScrolledRight = this.pillsList.scrollLeft !== 0;

      if (isListScrolledRight) {
        this.pillsList.classList.add(this.marginLeftArrowClass);
      } else {
        this.pillsList.classList.remove(this.marginLeftArrowClass);
      }
      this.scrollLeftButton.style.visibility = isListScrolledRight ? 'visible' : 'hidden';

      if (this.pillsList.lastElementChild?.getBoundingClientRect().right > this.limitRight) {
        this.scrollRightButton.style.visibility = 'visible';
      } else {
        this.scrollRightButton.style.visibility = 'hidden';
      }
    });
  }

  /**
   * Transition function for smooth scrolling.
   * @param {number} time The current time of the transition, in milliseconds.
   * @param {number} initialValue The initial value of the transition.
   * @param {number} changeValue The total change in value over the duration of the transition.
   * @param {number} transitionDuration The total duration of the transition in milliseconds.
   * @returns {number} The new value of the transition at the given time.
   */
  static easeInOut(time, initialValue, changeValue, transitionDuration) {
    let newTime = time;
    newTime /= transitionDuration / 2;
    if (newTime < 1) {
      return (changeValue / 2) * newTime * newTime + initialValue;
    }
    newTime -= 1;
    return (-changeValue / 2) * (newTime * (newTime - 2) - 1) + initialValue;
  }
}
